import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { api } from '@frontend/api/client';
import type { ISODate } from '@shared/types';

/**
 * The authorisation DECISION, not the raw record.
 *
 * `authorized` is the answer; `message` explains it; `continuation` is set when a
 * statutory bridge (cap-gap, H-1B portability, EAD auto-extension) is what makes today
 * lawful even though the document on file has expired. That distinction is why the UI
 * must never derive "expired therefore blocked" from `expiryDate` on its own — a person
 * on a valid continuation would be wrongly told they cannot work.
 */
export interface AuthorizationDecision {
  employeeId: string;
  employeeLabel: string;
  date: ISODate;
  authorized: boolean;
  reason: string;
  status: string | null;
  authorizationId: string | null;
  authTypeLabel: string | null;
  documentNumberMasked: string | null;
  documentExpiryDate: ISODate | null;
  authorizationEndDate: ISODate | null;
  daysRemaining: number | null;
  continuation: { basis: string; explanation: string; endDate: ISODate | null } | null;
  warning: string | null;
  message: string;
}

export interface WorkAuthRecord {
  id: string;
  employeeId: string;
  authType: string;
  status: string;
  documentNumberMasked: string | null;
  validFrom: ISODate | null;
  expiryDate: ISODate | null;
  isEmployerSpecific: boolean;
  sponsoredByCompany: boolean;
  i9Status: string;
  reverificationDueDate: ISODate | null;
  verifiedAt: string | null;
  notes?: string | null;
}

export interface MyWorkAuth {
  records: WorkAuthRecord[];
  decision: AuthorizationDecision;
  /** Convenience for the banner — the governing record, when there is one. */
  record?: WorkAuthRecord | null;
}

export function useMyWorkAuth(): UseQueryResult<MyWorkAuth & { record: WorkAuthRecord | null }> {
  return useQuery({
    queryKey: ['work-auth', 'mine'],
    queryFn: async () => {
      const response = await api.get<MyWorkAuth>('/work-auth/mine');
      const record =
        response.records.find((row) => row.id === response.decision.authorizationId) ??
        response.records[0] ??
        null;
      return { ...response, record };
    },
    retry: false,
  });
}

export interface DashboardRow {
  employeeId: string;
  employeeLabel: string;
  authorizationId: string | null;
  authType: string | null;
  authTypeLabel: string | null;
  documentNumberMasked: string | null;
  documentExpiryDate: ISODate | null;
  authorizationEndDate: ISODate | null;
  daysUntilDocumentExpiry: number | null;
  sponsoredByCompany: boolean;
  continuationBasis: string | null;
  continuationExplanation: string | null;
  i9Status: string | null;
  reverificationDueDate: ISODate | null;
  verified: boolean;
  reason: string;
}

export interface ComplianceDashboard {
  asOf: ISODate;
  expiringWindowDays: number;
  totals: {
    employees: number;
    tracked: number;
    notRequired: number;
    active: number;
    expiringSoon: number;
    unauthorized: number;
    continuedPastDocumentExpiry: number;
    pendingVerification: number;
    unverified: number;
    withoutRecord: number;
    reverificationDue: number;
  };
  expiring: DashboardRow[];
  unauthorized: DashboardRow[];
  continued: DashboardRow[];
  reverificationDue: DashboardRow[];
  withoutRecord: { employeeId: string; employeeLabel: string }[];
  sponsored: { authType: string; authTypeLabel: string; count: number }[];
}

export function useWorkAuthDashboard(): UseQueryResult<ComplianceDashboard> {
  return useQuery({
    queryKey: ['work-auth', 'dashboard'],
    queryFn: () => api.get<ComplianceDashboard>('/work-auth/dashboard'),
  });
}

/**
 * Somebody else's authorisation, as the employee record needs it.
 *
 * ── The crash this replaces ──────────────────────────────────────────────────
 *
 * `/api/work-auth/employee/:id` returns `{ employeeId, records, authorizationIntervals,
 * continuations }` and NO `decision`. `/work-auth/mine` DOES return one, so the shape the
 * hook was written against is real — just not on this route. `EmployeeDetail` read
 *
 *     workAuth.data?.records.find((row) => row.id === workAuth.data?.decision.authorizationId)
 *
 * where the `?.` guards `data` and NOT `decision`. That is `undefined.authorizationId`,
 * which throws, which the ErrorBoundary catches — so THE EMPLOYEE DETAIL SCREEN WAS
 * UNUSABLE FOR EVERY EMPLOYEE, FOR EVERY USER, and took the profile, reporting,
 * compensation and history tabs down with it.
 *
 * ── Why this composes rather than guards ─────────────────────────────────────
 *
 * A guard alone would leave `decision` permanently `null` here, and `WorkAuthBanner`
 * documents why that is not acceptable: it must NEVER derive "expired" from `expiryDate`,
 * because a cap-gap, an H-1B portability transfer or an EAD auto-extension makes today
 * lawful even when the document on file has lapsed. Only `decision.authorized` answers
 * the question. So the decision is fetched from the sibling `/status` route — same
 * permission, same `evaluateAuthorization` call — and merged in.
 *
 * The `/status` fetch is tolerated failing: a missing decision degrades the banner to the
 * date-based renewal notices, which is a worse answer but never a wrong one. The screen
 * itself must not depend on it.
 *
 * `/work-auth/employee/:id` returning `decision` directly would remove the second request
 * entirely, and is the better fix — but that route belongs to `work-auth-agent`.
 */
export interface EmployeeWorkAuthView {
  records: WorkAuthRecord[];
  /** `null` when the decision could not be fetched — NEVER assume authorised. */
  decision: AuthorizationDecision | null;
  /** The governing record, when there is one. */
  record: WorkAuthRecord | null;
}

/**
 * Record somebody's right to work.
 *
 * This is the gate on staffing: `assertAuthorizedForAssignment` refuses a project
 * assignment for anyone with no authorisation record, with the message "HR must add one …
 * before this employee can be assigned to a project". Before this hook existed there was no
 * way to add one, so on a fresh install NOBODY could be staffed to anything — the create
 * chain stopped one step short of being useful.
 *
 * Citizens and lawful permanent residents need no expiry date and resolve to
 * `NOT_REQUIRED`; every other category is date-tracked and alerted on.
 */
export function useCreateWorkAuth(): ReturnType<
  typeof useMutation<Record<string, unknown>, unknown, Record<string, unknown>>
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<Record<string, unknown>>('/work-auth', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['work-auth'] });
      void queryClient.invalidateQueries({ queryKey: ['employees'] });
      // The assignment pre-flight caches its answer; it is now a different answer.
      void queryClient.invalidateQueries({ queryKey: ['projects', 'assign-preview'] });
    },
  });
}

/**
 * Everything that can be DONE to a record, once one exists.
 *
 * Creating an authorisation was wired; nothing else was. Ten write routes had no caller,
 * which meant a record could be entered on the day someone joined and then never verified,
 * never re-verified when the document lapsed, never corrected after a typo, and never
 * revoked when the person left — the register was write-once. Those are not edge cases:
 * I-9 re-verification is a statutory obligation with a date on it, and a stale ACTIVE row
 * for a departed employee is a compliance finding.
 *
 * Every mutation here invalidates `['work-auth']` wholesale rather than patching a cache
 * entry, because the server RE-DERIVES status, continuation and re-verification date from
 * the change. Guessing the new status on the client is how the banner ends up telling a
 * lawfully-employed person they cannot work.
 *
 * Each mutation below spells its URL out at the `api.*` call site rather than routing
 * through a shared `path(id)` helper. That is not style: `verify:reachable` matches the
 * literal template argument of `api.post` / `api.patch`, so a path assembled behind an
 * indirection is invisible to it and the route still reports as having no caller. A gate
 * that cannot see a real caller is a gate that gets ignored.
 */
function useWorkAuthInvalidation(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['work-auth'] });
    // Staffing eligibility is downstream of every one of these.
    void queryClient.invalidateQueries({ queryKey: ['projects', 'assign-preview'] });
  };
}

export interface UpdateWorkAuthBody {
  authType?: string;
  documentNumber?: string | null;
  petitionNumber?: string | null;
  validFrom?: ISODate | null;
  expiryDate?: ISODate | null;
  sponsoredByCompany?: boolean;
  sponsorshipCaseNumber?: string | null;
  attorneyContact?: string | null;
  notes?: string | null;
}

export function useUpdateWorkAuth(): ReturnType<
  typeof useMutation<Record<string, unknown>, unknown, { id: string; body: UpdateWorkAuthBody }>
> {
  const invalidate = useWorkAuthInvalidation();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateWorkAuthBody }) =>
      api.patch<Record<string, unknown>>(`/work-auth/${id}`, body),
    onSuccess: invalidate,
  });
}

/** HR attests they have seen the documents. Until this happens the employee cannot be activated. */
export function useVerifyWorkAuth(): ReturnType<
  typeof useMutation<
    Record<string, unknown>,
    unknown,
    { id: string; body: { notes?: string | null; i9CompletedAt?: ISODate | null } }
  >
> {
  const invalidate = useWorkAuthInvalidation();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: { notes?: string | null; i9CompletedAt?: ISODate | null };
    }) => api.post<Record<string, unknown>>(`/work-auth/${id}/verify`, body),
    onSuccess: invalidate,
  });
}

export type I9Status = 'NOT_STARTED' | 'SECTION_1' | 'SECTION_2' | 'COMPLETE' | 'REVERIFICATION_DUE';

export function useRecordI9(): ReturnType<
  typeof useMutation<
    Record<string, unknown>,
    unknown,
    { id: string; body: { i9Status: I9Status; i9CompletedAt?: ISODate | null } }
  >
> {
  const invalidate = useWorkAuthInvalidation();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: { i9Status: I9Status; i9CompletedAt?: ISODate | null };
    }) => api.post<Record<string, unknown>>(`/work-auth/${id}/i9`, body),
    onSuccess: invalidate,
  });
}

/**
 * A successor FILING — the H-1B transfer receipt, the STEM OPT application, the EAD renewal.
 *
 * This is the endpoint that keeps a lawfully-employed person working when their card lapses,
 * and the response says explicitly whether the filing bought anything. The caller must SHOW
 * that message rather than assume success: a filing made after the document expired extends
 * nothing, and the difference decides whether somebody may work tomorrow.
 */
export interface ContinuationResult extends Record<string, unknown> {
  continuation: { basis: string; explanation: string; endDate: ISODate | null } | null;
  message: string;
}

export function useRecordContinuation(): ReturnType<
  typeof useMutation<ContinuationResult, unknown, { id: string; body: Record<string, unknown> }>
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.post<ContinuationResult>(`/work-auth/${id}/continuation`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['work-auth'] });
      void queryClient.invalidateQueries({ queryKey: ['projects', 'assign-preview'] });
    },
  });
}

/** The new document arrived: supersede the old record rather than editing its expiry date. */
export function useReverifyWorkAuth(): ReturnType<
  typeof useMutation<Record<string, unknown>, unknown, { id: string; body: Record<string, unknown> }>
> {
  const invalidate = useWorkAuthInvalidation();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.post<Record<string, unknown>>(`/work-auth/${id}/reverify`, body),
    onSuccess: invalidate,
  });
}

export function useRevokeWorkAuth(): ReturnType<
  typeof useMutation<Record<string, unknown>, unknown, { id: string; body: { reason: string } }>
> {
  const invalidate = useWorkAuthInvalidation();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: { reason: string } }) =>
      api.post<Record<string, unknown>>(`/work-auth/${id}/revoke`, body),
    onSuccess: invalidate,
  });
}

/**
 * The two endpoints that return a real number.
 *
 * POST, so the value never lands in a URL, a proxy log or browser history; rate-limited;
 * and the caller must state a PURPOSE that is written into a HIGH severity audit entry
 * before the value comes back. The UI therefore cannot reveal on hover or on render — it
 * has to ask, which is the point. Nothing caches the result.
 */
export function useRevealNumber(
  kind: 'document-number' | 'petition-number',
): ReturnType<
  typeof useMutation<Record<string, string | null>, unknown, { id: string; purpose: string }>
> {
  return useMutation({
    mutationFn: ({ id, purpose }: { id: string; purpose: string }) =>
      kind === 'document-number'
        ? api.post<Record<string, string | null>>(`/work-auth/${id}/document-number`, { purpose })
        : api.post<Record<string, string | null>>(`/work-auth/${id}/petition-number`, { purpose }),
  });
}

export interface WorkAuthAlert {
  id: string;
  authorizationId: string;
  daysBefore: number | null;
  firedAt: string;
  acknowledgedAt?: string | null;
  acknowledgedBy?: string | null;
  recipientCount?: number | null;
  severity?: string | null;
  message?: string | null;
}

export function useWorkAuthAlerts(authorizationId: string | null): UseQueryResult<{ alerts: WorkAuthAlert[] }> {
  return useQuery({
    queryKey: ['work-auth', 'alerts', authorizationId],
    queryFn: () => api.get<{ alerts: WorkAuthAlert[] }>(`/work-auth/${authorizationId ?? ''}/alerts`),
    enabled: authorizationId !== null && authorizationId !== '',
    retry: false,
  });
}

export function useAcknowledgeAlert(): ReturnType<
  typeof useMutation<Record<string, unknown>, unknown, { alertId: string; note?: string | null }>
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ alertId, note }: { alertId: string; note?: string | null }) =>
      api.post<Record<string, unknown>>(`/work-auth/alerts/${alertId}/acknowledge`, {
        note: note ?? null,
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['work-auth'] }),
  });
}

/**
 * Run the expiry sweep now.
 *
 * The cron runs daily, which means an HR user who has just loaded thirty visa records has
 * to wait until tomorrow to find out what that produced. Idempotent by design — running it
 * twice notifies nobody twice — so exposing it costs nothing and closes the loop.
 */
export function useRunWorkAuthAlerts(): ReturnType<
  typeof useMutation<{ fired?: number; notified?: number }, unknown, { asOf?: ISODate } | void>
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { asOf?: ISODate } | void) =>
      api.post<{ fired?: number; notified?: number }>('/work-auth/alerts/run', body ?? {}),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['work-auth'] }),
  });
}

export function useEmployeeWorkAuth(employeeId: string | null): UseQueryResult<EmployeeWorkAuthView> {
  return useQuery({
    queryKey: ['work-auth', 'employee', employeeId],
    queryFn: async () => {
      const id = employeeId ?? '';
      const [view, decision] = await Promise.all([
        api.get<{ records?: WorkAuthRecord[] }>(`/work-auth/employee/${id}`),
        api
          .get<AuthorizationDecision>(`/work-auth/employee/${id}/status`)
          .catch(() => null),
      ]);
      const records = view.records ?? [];
      const record =
        (decision === null
          ? undefined
          : records.find((row) => row.id === decision.authorizationId)) ??
        records[0] ??
        null;
      return { records, decision, record };
    },
    enabled: employeeId !== null && employeeId !== '',
    retry: false,
  });
}
